import { useRef, useState } from 'react';
import { fetchQuery } from '@api/fetch-query';
import {
  IMessage,
  INlpRequest,
  INlpResponse,
  IQuestionOption,
  MessageType,
  TNullable,
} from '@common/types';
import { ChatLayout } from '@components/chat-layout';
import { ChatStream } from '@components/chat-stream';
import { QuestionInput } from '@components/question-input';
import { SelectTopic } from '@components/select-topic';
import { HttpStatus } from '@constants/http';
import useChat from '@hooks/use-chat';
import {
  convertQueryToMessage,
  convertResponseToMessage,
  CURRENT_INSTANCE,
  questionOptionToMessage,
} from '@lib/utils';
import { useTranslation } from 'react-i18next';

function ChatPage() {
  const { t } = useTranslation();
  const { topics, setSelectedTopic, messages, addMessage, addResponse, responses } = useChat();
  const [query, setQuery] = useState<string>('');
  const [request, setRequest] = useState<TNullable<INlpRequest>>(null);
  const [showTopicsError, setShowTopicsError] = useState<boolean>(false);
  const [response, setResponse] = useState<INlpResponse | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const messageCounterRef = useRef<number>(messages.length * 2);

  function handleTopicSelect(topicId: string) {
    setSelectedTopic(topicId);
    setShowTopicsError(false);
    handleQuerySend(query, topicId);
  }
  function addMessageDelayed(message: IMessage, delay: number = 1000) {
    setLoading(true);
    setTimeout(() => {
      addMessage(message);
      setLoading(false);
    }, delay);
  }
  function handleMoreAnswersShow(messageId: number) {
    const currentResponse = responses.find((item) => item.id === messageId);
    if (currentResponse) {
      const choices = currentResponse?.response.questionOptions;
      if (!choices) {
        return;
      }

      const leftOverChoices = choices.slice(1);
      addMessageDelayed(
        {
          id: ++messageCounterRef.current,
          message: t('chat.request.show_more'),
          type: MessageType.UserEntry,
        },
        0
      );
      addMessageDelayed(
        {
          id: ++messageCounterRef.current,
          message: t('chat.response.perhaps_one_of_these'),
          choices: leftOverChoices,
          type: MessageType.ChoiceResponse,
        },
        2000
      );
    }
  }
  function handleChoiceSelect(choice: IQuestionOption | undefined) {
    if (!choice) {
      addMessageDelayed({
        id: ++messageCounterRef.current,
        type: MessageType.UserEntry,
        message: t('chat.request.contact_info_service'),
      });
      addMessageDelayed(
        { id: ++messageCounterRef.current, type: MessageType.ForceMedInfo, message: '' },
        2000
      );
      return;
    }
    if (response?.questionOptions) {
      response.questionOptions = response.questionOptions.filter((c) => c !== choice);
    }
    addMessageDelayed({
      id: ++messageCounterRef.current,
      type: MessageType.UserEntry,
      message: t('chat.request.show_question') + choice.actualQuery,
    });
    addMessageDelayed(
      questionOptionToMessage(choice, response?.query!.id || '', t, ++messageCounterRef.current),
      2000
    );
  }
  function handleStaticMessage(message: string, type: MessageType) {
    messageCounterRef.current += 1;
    addMessage({
      message,
      type,
      id: messageCounterRef.current,
    });
  }
  async function handleQuerySend(query: string, topicId: string) {
    setLoading(true);
    setResponse(null);
    const request: INlpRequest = {
      query,
      topicId,
      instanceId: CURRENT_INSTANCE.name,
    };
    messageCounterRef.current += 1;
    addMessage(convertQueryToMessage(query, messageCounterRef.current));
    messageCounterRef.current += 1;
    setRequest(request);
    setQuery('');
    const result = await fetchQuery(request, t);
    if (result) {
      if (result.error) {
        if (result.status === HttpStatus.BadRequest) {
          handleStaticMessage('', MessageType.InvalidQuery);
          setLoading(false);
          return;
        }
        if (result.status === HttpStatus.NotFound) {
          setResponse(result);
          messageCounterRef.current += 1;
          const message = questionOptionToMessage(
            {
              status: 'infoservice',
              questionId: `random-${messageCounterRef.current}`,
              optionType: 'UNCERTAIN_OPTION',
              actualQuery: query,
              answer: '',
              paraphrases: [],
              sourceDocuments: [],
              similarity: 1,
            },
            `random-${messageCounterRef.current}`,
            t,
            messageCounterRef.current
          );
          addMessage(message);
          addResponse(messageCounterRef.current, result);
          setLoading(false);
          return;
        }
        if (result.status === HttpStatus.UnprocessableContent) {
          handleStaticMessage(result.error, MessageType.Error);
          setLoading(false);
          return;
        }
      }
      setResponse(result);
      messageCounterRef.current += 1;
      const message = convertResponseToMessage(result, ++messageCounterRef.current, t);
      addMessage(message);
      addResponse(messageCounterRef.current, result);
      setLoading(false);
    }
  }

  return (
    <ChatLayout>
      <ChatStream
        request={request}
        onShowMoreAnswers={handleMoreAnswersShow}
        onSelectChoice={handleChoiceSelect}
        isLoading={isLoading}
      />
      <QuestionInput
        query={query}
        setQuery={setQuery}
        setShowTopicsError={setShowTopicsError}
        topics={topics}
        addStaticMessage={handleStaticMessage}
        sendQuery={handleQuerySend}
        disabled={isLoading || showTopicsError}
      />
      {showTopicsError && <SelectTopic topics={topics} selectTopic={handleTopicSelect} />}
    </ChatLayout>
  );
}

export default ChatPage;
